import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api, { formatApiError, setAuthToken, clearAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data.token) setAuthToken(data.token);
      setUser(data);
    } catch {
      clearAuthToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) setAuthToken(data.token);
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name, phone, role, cniNumber, licenseNumber, referrerName, acceptedTerms = false, referralCode = "") => {
    const payload = { email, password, name, phone, role, referrer_name: referrerName || "", accepted_terms: !!acceptedTerms, referral_code: (referralCode || "").trim().toUpperCase() };
    if (role === "driver") {
      payload.cni_number = cniNumber || "";
      payload.license_number = licenseNumber || "";
    }
    const { data } = await api.post("/auth/register", payload);
    if (data.token) setAuthToken(data.token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Logout error:", err.message);
    }
    clearAuthToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, checkAuth }),
    [user, loading, login, register, logout, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
